import { useState, useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { QMS_DOMPURIFY_CONFIG } from '../../utils/qmsHtmlAllowlist';

// Reads one HTML version of a QMS document: the overview (the plain-English
// summary) or the full version (the controlled wording). Both bodies were
// sanitised on the way into the database; they are sanitised again here with the
// same allowlist before they touch the DOM, because the stored HTML outlives
// any one deploy.
//
// Acknowledgement is a full-version matter. The viewer reports reaching the end
// of the FULL version through onBottom, exactly as PdfScrollViewer does for a
// PDF, and never for the overview: reading the gist is not reading the policy.

export const clean = (html) => DOMPurify.sanitize(html || '', QMS_DOMPURIFY_CONFIG);

/** Opens a print-ready copy of the full version in a new window. */
export function printDocument({ doc, content }) {
  const w = window.open('', '_blank', 'noopener');
  if (!w) return;
  const meta = [
    ['Document', doc?.doc_number],
    ['Version', content?.version_number],
    ['Owner', doc?.owner_name],
    ['Live from', content?.live_at ? formatDate(content.live_at) : null],
    ['Next review', doc?.next_review_date ? formatDate(doc.next_review_date) : null],
  ].filter(([, v]) => v);
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  w.document.write(`<!doctype html><html lang="en-GB"><head><meta charset="utf-8"><title>${esc(doc?.title || 'Document')}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24mm 20mm; line-height: 1.5; font-size: 11pt; }
  .ctl { display: flex; flex-wrap: wrap; gap: 6px 24px; font-size: 9pt; color: #555; border-left: 3px solid #A1723D; padding: 6px 12px; margin-bottom: 20px; }
  .ctl b { color: #0D1D32; }
  h1 { font-size: 18pt; color: #0D1D32; border-bottom: 2px solid #A1723D; padding-bottom: 6px; margin: 0 0 16px; }
  h2 { font-size: 13pt; color: #0D1D32; margin: 20px 0 6px; }
  h3 { font-size: 11.5pt; color: #0D1D32; margin: 16px 0 4px; }
  p { margin: 0 0 10px; } ul, ol { margin: 0 0 10px; padding-left: 24px; } li { margin: 3px 0; }
  ol ol { list-style-type: lower-alpha; } ol ol ol { list-style-type: lower-roman; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0 14px; font-size: 10pt; }
  th, td { border: 1px solid #bbb; padding: 4px 8px; text-align: left; vertical-align: top; } th { background: #f3f0e9; }
  blockquote { border-left: 3px solid #ddd; margin: 0 0 10px; padding: 2px 12px; color: #444; }
  img { max-width: 100%; }
  footer { position: fixed; bottom: 8mm; left: 20mm; right: 20mm; font-size: 8pt; color: #777; border-top: 1px solid #ddd; padding-top: 4px; }
</style></head><body>
<div class="ctl">${meta.map(([k, v]) => `<span>${esc(k)}: <b>${esc(v)}</b></span>`).join('')}</div>
${clean(content?.html_content)}
<footer>${esc(doc?.doc_number || '')} v${esc(content?.version_number || '')}. Printed ${formatDate(new Date().toISOString())}. The live version in the ops platform is the controlled copy.</footer>
</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 250);
}

export default function HtmlDocumentView({ doc, versionId, onBottom, initialMode, showControls = true }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(initialMode || null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const onBottomRef = useRef(onBottom);
  onBottomRef.current = onBottom;

  useEffect(() => {
    if (!doc?.id || !versionId) return;
    let cancelled = false;
    setContent(null);
    setError('');
    api.get(`qms/documents/${doc.id}/versions/${versionId}/content`)
      .then((c) => {
        if (cancelled) return;
        setContent(c);
        if (!mode) setMode(c.summary_html && c.summary_html.trim() ? 'overview' : 'full');
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load document'); });
    return () => { cancelled = true; };
  }, [doc?.id, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasOverview = !!(content?.summary_html && content.summary_html.trim());
  const fullHtml = useMemo(() => clean(content?.html_content), [content?.html_content]);
  const overviewHtml = useMemo(() => clean(content?.summary_html), [content?.summary_html]);

  // The end of the FULL version is what counts as having read the document.
  useEffect(() => {
    if (!content || mode !== 'full' || !sentinelRef.current || !scrollRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && onBottomRef.current) { onBottomRef.current(); obs.disconnect(); } },
      { root: scrollRef.current, threshold: 0.1 },
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [content, mode]);

  if (error) {
    return <div className="flex-1 flex items-center justify-center text-[11px] text-red-500 p-6">{error}</div>;
  }
  if (!content) {
    return <div className="flex-1 flex items-center justify-center text-[11px] text-gray-400 p-6">Loading document…</div>;
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-50">
      {showControls && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0 flex-wrap">
          {hasOverview ? (
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="tablist" aria-label="Version to read" data-tour="qms-view-switch">
              <button
                role="tab"
                aria-selected={mode === 'overview'}
                onClick={() => setMode('overview')}
                className={`px-3 py-1 text-[11px] font-semibold transition-colors ${mode === 'overview' ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:text-navy'}`}
              >
                Overview
              </button>
              <button
                role="tab"
                aria-selected={mode === 'full'}
                onClick={() => setMode('full')}
                className={`px-3 py-1 text-[11px] font-semibold transition-colors border-l border-gray-200 ${mode === 'full' ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:text-navy'}`}
              >
                Full version
              </button>
            </div>
          ) : (
            <span className="text-[10px] text-gray-400">Full version</span>
          )}
          {mode === 'overview' && (
            <span className="text-[9px] text-gray-400">A short summary. The full version is the controlled wording.</span>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => printDocument({ doc, content })}
            className="text-[10px] text-gray-500 hover:text-navy border border-gray-200 rounded-lg px-2.5 py-1 transition-colors"
            title="Print the full version, or save it as a PDF"
          >
            Print / PDF
          </button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-6">
          {/* Control block: who owns it, which version this is, when it changed */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-gray-500 border-l-2 border-gold pl-3 mb-6">
            <span>Document <b className="text-navy font-mono">{doc?.doc_number}</b></span>
            <span>Version <b className="text-navy">{content.version_number}</b></span>
            {doc?.owner_name && <span>Owner <b className="text-navy">{doc.owner_name}</b></span>}
            {content.live_at && <span>Live from <b className="text-navy">{formatDate(content.live_at)}</b></span>}
            {doc?.next_review_date && <span>Next review <b className="text-navy">{formatDate(doc.next_review_date)}</b></span>}
            {content.status && content.status !== 'live' && (
              <span className="text-amber-700 font-semibold uppercase text-[9px]">{content.status.replace('_', ' ')}</span>
            )}
          </div>

          {mode === 'overview' ? (
            <div className="qms-doc qms-doc-overview" dangerouslySetInnerHTML={{ __html: overviewHtml }} />
          ) : (
            <div className="qms-doc" dangerouslySetInnerHTML={{ __html: fullHtml }} />
          )}

          {mode === 'overview' && (
            <div className="mt-8 pt-4 border-t border-gray-200 text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
              <span>This is the overview.</span>
              <button type="button" onClick={() => setMode('full')} className="text-gold font-semibold hover:underline">Read the full version</button>
            </div>
          )}
          {mode === 'full' && <div ref={sentinelRef} style={{ height: 1 }} />}
        </div>
      </div>
    </div>
  );
}
